// =============================================================
// onepage.js — entry point for the onepage A/B variant.
// Reuses md-loader, lightbox, slideshow, audience spotlight,
// WhatsApp FAB. Skips slider/knob. Adds sticky anchor nav with
// scroll-spy and IntersectionObserver-based reveal animations.
// =============================================================

import { loadAllSections, getWhatsappFromData } from "./md-loader.mjs";
import { initLightbox } from "./lightbox.js";
import { initSlideshows, activateSlideshowsIn } from "./slideshow.js";
import { playIntroIndex } from "./intro-index.js";

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

const lang = typeof localStorage !== 'undefined' ? (localStorage.getItem('siteLang') || 'en') : 'en';

const SECTION_LABELS = lang === 'es' ? {
  "section-hero": "Inicio",
  "section-experience": "Experiencia",
  "section-gallery": "Galería",
  "section-perfect-for": "Perfecto Para",
  "section-about": "Sobre Mí",
  "section-blog": "Blog",
  "section-contact": "Contacto",
} : {
  "section-hero": "Home",
  "section-experience": "Experience",
  "section-gallery": "Gallery",
  "section-perfect-for": "Perfect For",
  "section-about": "About",
  "section-blog": "Blog",
  "section-contact": "Contact",
};

// Wait for CDN globals (marked + gsap + ScrollTrigger)
function waitForGlobals(timeout = 4000) {
  return new Promise((resolve) => {
    const start = Date.now();
    (function check() {
      if (window.marked && window.gsap && window.ScrollTrigger) return resolve();
      if (Date.now() - start > timeout) return resolve();
      requestAnimationFrame(check);
    })();
  });
}

// Pause/play hero video depending on whether the hero section is visible.
function manageHeroVideo(activeEl) {
  const activeVideo = activeEl.querySelector("video.hero-video");
  $$(".slide video.hero-video").forEach((v) => {
    if (v === activeVideo) v.play?.().catch(() => {});
    else v.pause?.();
  });
}

// Mouse-tracked spotlight on .audience-card (same logic as main.js).
function bindAudienceSpotlight(root = document) {
  const cards = root.querySelectorAll(".audience-card");
  cards.forEach((card) => {
    card.addEventListener("pointermove", (e) => {
      const r = card.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      card.style.setProperty("--mx", `${x}%`);
      card.style.setProperty("--my", `${y}%`);
    });
    card.addEventListener("pointerleave", () => {
      card.style.removeProperty("--mx");
      card.style.removeProperty("--my");
    });
  });
}

// Floating WhatsApp button — built from contact frontmatter.
function setupWhatsappFab(allData) {
  const fab = $("#whatsapp-fab");
  if (!fab) return;
  const wa = getWhatsappFromData(allData);
  if (!wa) {
    fab.hidden = true;
    return;
  }
  fab.href = wa.href;
  fab.querySelector(".fab-label").textContent = wa.label;
  fab.hidden = false;

  if (window.gsap) {
    window.gsap.from(fab, {
      opacity: 0,
      scale: 0.5,
      y: 20,
      duration: 0.8,
      delay: 1.2,
      ease: "back.out(1.8)",
    });
  }
}

// Build sticky right-side anchor nav with one dot per section.
function buildAnchorNav(slides) {
  const nav = document.createElement("nav");
  nav.className = "anchor-nav";
  nav.setAttribute("aria-label", "Section navigation");

  slides.forEach((s) => {
    const id = s.id;
    const label = SECTION_LABELS[id] || id;
    const a = document.createElement("a");
    a.className = "anchor-link";
    a.href = `#${id}`;
    a.dataset.label = label;
    a.setAttribute("aria-label", `Go to ${label}`);
    nav.appendChild(a);
  });

  document.body.appendChild(nav);
  return nav;
}

// IntersectionObserver-based scroll-spy: highlights the link of the
// most-visible section, manages hero video, activates slideshows.
function bindScrollSpy(slides, nav) {
  const links = $$(".anchor-link", nav);
  const linkById = new Map();
  slides.forEach((s, i) => linkById.set(s.id, links[i]));

  // Track visibility ratio per slide so we can pick the top one even
  // when multiple sections overlap the viewport.
  const ratios = new Map(slides.map((s) => [s.id, 0]));

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        ratios.set(e.target.id, e.isIntersecting ? e.intersectionRatio : 0);
      });

      // Pick id with max ratio.
      let bestId = null;
      let bestR = 0;
      ratios.forEach((r, id) => {
        if (r > bestR) {
          bestR = r;
          bestId = id;
        }
      });
      if (!bestId) return;

      // Update active link.
      links.forEach((l) => l.classList.remove("is-active"));
      const active = linkById.get(bestId);
      if (active) active.classList.add("is-active");

      // Hero video + slideshows track the active section.
      const activeSlide = document.getElementById(bestId);
      if (activeSlide) {
        manageHeroVideo(activeSlide);
        activateSlideshowsIn(activeSlide);
      }
    },
    {
      // Multiple thresholds for finer ratio updates.
      threshold: [0, 0.15, 0.3, 0.5, 0.7, 0.9, 1],
    }
  );

  slides.forEach((s) => io.observe(s));
}

// First-time reveal animation per slide using IntersectionObserver.
function bindRevealOnIntersect(slides) {
  if (!window.gsap) return;
  const seen = new WeakSet();

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const slide = e.target;
        if (seen.has(slide)) return;
        seen.add(slide);

        const targets = $$("[data-reveal], [data-split]", slide);
        if (!targets.length) return;

        window.gsap.set(targets, { opacity: 0, y: 24 });
        window.gsap.to(targets, {
          opacity: 1,
          y: 0,
          duration: 0.85,
          ease: "expo.out",
          stagger: 0.05,
          delay: 0.1,
        });

        const splits = $$("[data-split]", slide);
        if (window.SplitText && splits.length) {
          splits.forEach((el) => {
            try {
              const split = new window.SplitText(el, { type: "chars,words" });
              window.gsap.from(split.chars, {
                opacity: 0,
                y: 32,
                rotateX: -45,
                stagger: 0.022,
                duration: 0.65,
                ease: "back.out(1.6)",
                delay: 0.2,
              });
            } catch (_) {
              /* swallow */
            }
          });
        }
      });
    },
    { threshold: 0.2 }
  );

  slides.forEach((s) => io.observe(s));
}

function logoEntrance() {
  if (!window.gsap) return;
  window.gsap.from(".logo-mark", {
    opacity: 0,
    scale: 0.4,
    rotation: -30,
    duration: 0.9,
    ease: "back.out(1.6)",
    clearProps: "opacity,scale,rotation,transform",
  });
}

// Smooth scroll for anchor links (extra layer in addition to CSS scroll-behavior).
function bindAnchorClicks(nav) {
  nav.addEventListener("click", (e) => {
    const link = e.target.closest(".anchor-link");
    if (!link) return;
    const id = link.getAttribute("href").slice(1);
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    // Update hash without jumping.
    history.replaceState(null, "", `#${id}`);
  });
}

function initLangSwitch() {
  const urlParams = new URLSearchParams(window.location.search);
  const paramLang = urlParams.get('lang');
  const currentLang = paramLang || (typeof localStorage !== 'undefined' ? localStorage.getItem('siteLang') : null) || 'en';
  
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('siteLang', currentLang);
  }

  // Update HTML lang attribute for SEO/accessibility
  document.documentElement.lang = currentLang;

  const links = $$('.lang-btn');
  
  links.forEach(link => {
    // Add active class
    link.classList.toggle('active', link.dataset.lang === currentLang);
    
    // Build correct URL
    const targetLang = link.dataset.lang;
    const url = new URL(window.location.href);
    if (targetLang === 'en') {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', targetLang);
    }
    link.href = url.toString();
  });
}

// Boot
(async function init() {
  await waitForGlobals();

  if (window.marked) {
    window.marked.setOptions({ gfm: true, breaks: true });
  }

  const slidesRoot = $(".slides-track");
  const { data: allData } = await loadAllSections(slidesRoot);

  const slides = $$(".slide", slidesRoot);

  const nav = buildAnchorNav(slides);
  bindScrollSpy(slides, nav);
  bindAnchorClicks(nav);

  initLightbox(document);
  initSlideshows(document);
  bindAudienceSpotlight(document);
  setupWhatsappFab(allData);
  initLangSwitch();
  bindRevealOnIntersect(slides);

  // Parallax for hero background image
  if (window.gsap && window.ScrollTrigger) {
    window.gsap.registerPlugin(window.ScrollTrigger);
    
    // Scale up slightly so it doesn't reveal edges when moving down
    const heroBg = $(".slide[data-type='hero'] .hero-video");
    if (heroBg) {
      window.gsap.set(heroBg, { scale: 1.15 });
      window.gsap.to(heroBg, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: ".slide[data-type='hero']",
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });
    }
  }

  // Prime hero video + first-section slideshows.
  if (slides.length) {
    manageHeroVideo(slides[0]);
    activateSlideshowsIn(slides[0]);
    nav.querySelector(".anchor-link")?.classList.add("is-active");
  }

  logoEntrance();

  // Intro index splash (3s, sweep B→T) — hides .anchor-nav during play.
  playIntroIndex({
    orientation: "vertical",
    duration: 3000,
    hideEl: nav,
  });

  // Hash-based deep link (after content is loaded).
  if (location.hash) {
    const target = document.querySelector(location.hash);
    if (target) {
      // Defer one frame so layout settles.
      requestAnimationFrame(() =>
        target.scrollIntoView({ behavior: "smooth", block: "start" })
      );
    }
  }
})();
